import { AriseBridgeHost } from '@/arise/bridge/AriseBridgeHost';
import { Badge, ProfileV2, Title } from '@/models/profile';
import { Translations } from '@/models/translations';
import { useAppSelector } from '@/state/store';
import { useEffect, useRef } from 'react';

export default function usePushUserData(bridge: AriseBridgeHost) {
    const userDataRef = useRef<{
        langIso: null | string;
        currentUserProfileV2: null | ProfileV2;
        availableBadges?: null | Badge[];
        availableTitles?: null | Title[];
        translations?: null | Translations;
    }>(null);

    const currentUserProfileV2 = useAppSelector((state) => state.profile.currentUserProfileV2);
    const availableBadges = useAppSelector((state) => state.profile.availableBadges);
    const availableTitles = useAppSelector((state) => state.profile.availableTitles);
    const langIso = useAppSelector((state) => state.app.langIso);
    const translations = useAppSelector((state) => state.app.translations);

    userDataRef.current = {
        langIso,
        currentUserProfileV2,
        availableBadges,
        availableTitles,
        translations,
    };

    function pushUserProfile() {
        if (userDataRef.current.currentUserProfileV2) {
            bridge?.sendEvent('user-data', userDataRef.current.currentUserProfileV2);
        }
    }

    function pushAvailableBadgesData() {
        if (userDataRef.current.availableBadges) {
            bridge?.sendEvent('available-badges-data', userDataRef.current.availableBadges);
        }
    }

    function pushAvailableTitlesData() {
        if (userDataRef.current.availableTitles) {
            bridge?.sendEvent('available-titles-data', userDataRef.current.availableTitles);
        }
    }

    function pushLocale() {
        if (userDataRef.current.langIso) {
            bridge?.sendEvent('locale', { locale: userDataRef.current.langIso });
        }
    }

    function pushTranslations() {
        if (userDataRef.current.translations) {
            bridge?.sendEvent('translations', { translations: userDataRef.current.translations });
        }
    }

    function pushUserData() {
        pushUserProfile();
        pushAvailableBadgesData();
        pushAvailableTitlesData();
        pushLocale();
        pushTranslations();
    }

    useEffect(() => {
        pushUserProfile();
    }, [currentUserProfileV2]);

    useEffect(() => {
        pushAvailableBadgesData();
    }, [availableBadges]);

    useEffect(() => {
        pushAvailableTitlesData();
    }, [availableTitles]);

    useEffect(() => {
        pushLocale();
    }, [langIso]);

    useEffect(() => {
        pushTranslations();
    }, [translations]);

    return { pushUserData, pushLocale, pushTranslations };
}
